// src/pages/document/DocumentTemplateEditorPage.tsx
// Template editor: edit a template's metadata + body, drop clause bodies in,
// declare default tags, instantiate documents from it, and verify entity binding.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { observer } from '@react';
import { useDocument, useI18n } from '@react/hooks';
import { useScope } from '@/hooks/useScope';
import { ErrorAlert, Badge } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { clauseStatusMap, mapping, parseList } from '@/features/document/complianceLogic';

export const DocumentTemplateEditorPage = observer(() => {
  const { t } = useI18n();
  const { templateId } = useParams<{ templateId: string }>();
  const store = useDocument();
  const scope = useScope();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // editable fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [titlePattern, setTitlePattern] = useState('');
  const [body, setBody] = useState('');
  const [tags, setTags] = useState('');

  // clause insertion
  const [clauses, setClauses] = useState<any[]>([]);
  const [clausePickerOpen, setClausePickerOpen] = useState(false);

  // instantiate
  const [instValues, setInstValues] = useState<Record<string, string>>({});
  const [instTitle, setInstTitle] = useState('');

  // entity binding check
  const [bindType, setBindType] = useState('');
  const [bindId, setBindId] = useState('');
  const [bindResult, setBindResult] = useState<any[] | null>(null);

  const load = useCallback(() => {
    if (!templateId) return;
    setLoading(true);
    setError(null);
    store
      .GetDocumentTemplate(templateId)
      .then((res: any) => {
        const tpl = res?.template ?? res;
        setTemplate(tpl);
        setName(tpl?.name || '');
        setDescription(tpl?.description || '');
        setTitlePattern(tpl?.default_title_pattern || '');
        setBody(tpl?.default_content || '');
        setTags((tpl?.default_tags || []).join(', '));
      })
      .catch((e: unknown) => setError(e))
      .finally(() => setLoading(false));
  }, [templateId, scope.productId, store]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!template || !templateId) return;
    setBusy('save');
    try {
      await store.UpdateDocumentTemplate(templateId, {
        template: {
          ...template,
          name,
          description,
          default_title_pattern: titlePattern,
          default_content: body,
          default_tags: parseList(tags),
        },
      } as any);
      load();
    } catch { /* store */ } finally { setBusy(null); }
  };

  const openClausePicker = () => {
    setClausePickerOpen(true);
    if (clauses.length === 0) {
      store.ListClauses().then((res: any) => setClauses(res?.clauses?.items ?? [])).catch(() => {});
    }
  };

  const insertClause = (c: any) => {
    setBody((prev) => `${prev}${prev && !prev.endsWith('\n') ? '\n\n' : ''}${c.body}`);
    setClausePickerOpen(false);
  };

  const remove = async () => {
    if (!templateId) return;
    setBusy('delete');
    try {
      await store.DeleteDocumentTemplate(templateId);
      navigate('/documents/templates');
    } catch { /* store */ } finally { setBusy(null); }
  };

  const instantiate = async () => {
    if (!templateId) return;
    setBusy('instantiate');
    try {
      const res: any = await store.CreateDocumentFromTemplate(templateId, {
        template: template?.entity,
        title_override: instTitle.trim() || undefined,
        variable_values: instValues,
      } as any);
      const docId = res?.entity?.id;
      if (docId) navigate(`/documents/${docId}`);
    } catch { /* store */ } finally { setBusy(null); }
  };

  const checkBinding = async () => {
    if (!bindType.trim() || !bindId.trim()) return;
    setBusy('bind');
    try {
      const res: any = await store.FindTemplatesForEntity(bindType.trim(), bindId.trim());
      setBindResult(res?.templates?.items ?? res?.templates ?? []);
    } catch {
      setBindResult([]);
    } finally { setBusy(null); }
  };

  const variables: any[] = template?.variable_definitions ?? [];
  const inputCls = 'w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:ring-1 focus:ring-ring';

  if (loading) {
    return (
      <div className="max-w-[1000px] mx-auto px-6 py-8 space-y-3 animate-pulse" aria-busy="true">
        <div className="h-8 w-1/2 rounded bg-muted" />
        <div className="h-40 rounded bg-muted" />
      </div>
    );
  }
  if (error && !template) {
    return (
      <div className="max-w-[1000px] mx-auto px-6 py-8">
        <ErrorAlert error={error} />
        <Link to="/documents/templates" className="mt-4 inline-block text-sm text-primary hover:underline">{t('common.backToList')}</Link>
      </div>
    );
  }
  if (!template) {
    return (
      <div className="max-w-[1000px] mx-auto px-6 py-8 text-center text-muted-foreground">{t('common.notFound')}</div>
    );
  }

  return (
    <div className="max-w-[1100px] mx-auto px-6 py-6">
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground mb-4">
        <Link to="/documents/templates" className="hover:text-foreground transition-colors">{t('document.template.list')}</Link>
        <span>/</span>
        <span className="text-foreground truncate max-w-[280px]">{name || t('common.untitled')}</span>
      </nav>

      {error ? <div className="mb-4"><ErrorAlert error={error} dismissible onDismiss={() => setError(null)} /></div> : null}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-5">
        {/* Editor */}
        <div className="space-y-3">
          <input className={`${inputCls} text-lg font-semibold`} value={name} onChange={(e) => setName(e.target.value)} placeholder={t('document.template.fieldName')} aria-label={t('document.template.fieldName')} />
          <input className={inputCls} value={description} onChange={(e) => setDescription(e.target.value)} placeholder={t('document.template.fieldDescription')} aria-label={t('document.template.fieldDescription')} />
          <input className={inputCls} value={titlePattern} onChange={(e) => setTitlePattern(e.target.value)} placeholder={t('document.template.fieldTitlePattern')} aria-label={t('document.template.fieldTitlePattern')} />

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-medium text-foreground">{t('document.template.body')}</label>
              <button type="button" onClick={openClausePicker} className="text-xs text-primary hover:underline">
                {t('document.template.insertClause')}
              </button>
            </div>
            <textarea className={`${inputCls} font-mono resize-y`} rows={16} value={body} onChange={(e) => setBody(e.target.value)} placeholder={t('document.template.bodyPlaceholder')} aria-label={t('document.template.body')} />
          </div>

          <div>
            <label className="text-sm font-medium text-foreground">{t('document.template.defaultTags')}</label>
            <input className={inputCls} value={tags} onChange={(e) => setTags(e.target.value)} placeholder={t('document.template.defaultTagsPlaceholder')} aria-label={t('document.template.defaultTags')} />
          </div>

          <div className="flex gap-2 pt-1">
            <button type="button" disabled={busy !== null} onClick={save} className="px-4 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">
              {busy === 'save' ? t('common.saving') : t('common.save')}
            </button>
            <button type="button" onClick={() => setConfirmDelete(true)} className="px-4 py-2 text-sm rounded-md text-destructive hover:bg-destructive/10 transition-colors">
              {t('common.delete')}
            </button>
          </div>
        </div>

        {/* Side: instantiate + binding */}
        <div className="space-y-4">
          <div className="rounded-lg border border-border p-4 space-y-2.5">
            <h2 className="text-sm font-semibold text-foreground">{t('document.template.instantiate')}</h2>
            <input className={inputCls} value={instTitle} onChange={(e) => setInstTitle(e.target.value)} placeholder={t('document.template.instantiateTitle')} aria-label={t('document.template.instantiateTitle')} />
            {variables.length > 0 && (
              <div className="space-y-1.5">
                {variables.map((v) => (
                  <div key={v.key}>
                    <label className="text-xs text-muted-foreground">{v.label || v.key}{v.required ? ' *' : ''}</label>
                    <input
                      className={inputCls}
                      value={instValues[v.key] ?? v.default_value ?? ''}
                      onChange={(e) => setInstValues({ ...instValues, [v.key]: e.target.value })}
                      placeholder={v.description || v.key}
                      aria-label={v.label || v.key}
                    />
                  </div>
                ))}
              </div>
            )}
            <button type="button" disabled={busy !== null} onClick={instantiate} className="w-full px-3 py-2 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition-colors">
              {busy === 'instantiate' ? t('common.saving') : t('document.template.instantiateAction')}
            </button>
            <p className="text-[11px] text-muted-foreground">{t('document.template.usageCount', { n: template.usage_count ?? 0 })}</p>
          </div>

          <div className="rounded-lg border border-border p-4 space-y-2.5">
            <h2 className="text-sm font-semibold text-foreground">{t('document.template.bindingCheck')}</h2>
            <p className="text-xs text-muted-foreground">{t('document.template.bindingHint')}</p>
            <input className={inputCls} value={bindType} onChange={(e) => setBindType(e.target.value)} placeholder={t('document.template.bindingType')} aria-label={t('document.template.bindingType')} />
            <input className={inputCls} value={bindId} onChange={(e) => setBindId(e.target.value)} placeholder={t('document.template.bindingId')} aria-label={t('document.template.bindingId')} />
            <button type="button" disabled={busy !== null || !bindType.trim() || !bindId.trim()} onClick={checkBinding} className="w-full px-3 py-2 text-sm font-medium rounded-md border border-border text-foreground hover:bg-muted disabled:opacity-40 transition-colors">
              {busy === 'bind' ? t('common.loading') : t('document.template.bindingCheckAction')}
            </button>
            {bindResult !== null && (
              bindResult.length === 0 ? (
                <p className="text-xs text-muted-foreground">{t('document.template.bindingNone')}</p>
              ) : (
                <ul className="space-y-1">
                  {bindResult.map((tpl: any, i: number) => (
                    <li key={tpl.entity?.id || i} className="text-xs text-foreground truncate">• {tpl.name}</li>
                  ))}
                </ul>
              )
            )}
          </div>
        </div>
      </div>

      {/* Clause picker */}
      {clausePickerOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true" aria-label={t('document.template.insertClause')}>
          <div className="w-full max-w-lg max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-popover shadow-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-foreground">{t('document.template.pickClause')}</h3>
              <button type="button" onClick={() => setClausePickerOpen(false)} aria-label={t('common.close')} className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12" /></svg>
              </button>
            </div>
            {clauses.length === 0 ? (
              <p className="text-sm text-muted-foreground py-8 text-center">{t('document.clause.empty')}</p>
            ) : (
              <ul className="space-y-1.5">
                {clauses.map((c) => (
                  <li key={c.entity?.id}>
                    <button type="button" onClick={() => insertClause(c)} className="w-full text-left rounded-md border border-border p-2.5 hover:bg-muted/40 transition-colors">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-foreground truncate flex-1">{c.title}</span>
                        <Badge variant={mapping(clauseStatusMap, c.status).variant as any} size="sm">{t(mapping(clauseStatusMap, c.status).labelKey)}</Badge>
                      </div>
                      <div className="text-xs text-muted-foreground truncate">{c.summary || c.body}</div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('document.template.deleteTitle')}
        description={t('document.template.deleteBody')}
        confirmLabel={t('common.delete')}
        destructive
        isConfirming={busy === 'delete'}
        onConfirm={remove}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
});

export default DocumentTemplateEditorPage;
